import React from 'react';
import _ from 'lodash';

import { getBillingDashboardPageBreadcrumb } from '../Billing/BillingDashboardPage';

import Field from '../../Fields/Field';
import InputTextField from '../../Fields/InputTextField';
import DataTable from '../../Fields/DataTable';

import ProcessingIndicator from '../../Common/ProcessingIndicator';
import PageHeader from '../../Common/PageHeader';
import NavigationBreadcrumbs from '../../Common/NavigationBreadcrumbs';

import {
  queryListInvoices,
  queryGetInvoiceReport,
  queryResendInvoiceEmail,
  queryGetInvoicePDF,
} from '../../../Queries';

import { getFacility } from '../../../Utils/facility';

import featureFlags from '../../../featureFlags';

import './InvoiceListPage.scss';

const oldInvoicesQueryFields = [
  'total',
  'limit',
  'offset',
  {'invoices': [
      'id',
      'readable_id',
      'amount',
      'balance',
      'created_at',
      {'payor': [
          'id',
          'readable_id',
          'billing_firstname',
          'billing_lastname',
          'info_name'
        ]
      }
    ]
  }
];

const invoiceQueryFields = featureFlags.SUBSCRIPTION_NAME ? [
  'total',
  'limit',
  'offset',
  {'invoices': [
      'id',
      'readable_id',
      'amount',
      'balance',
      'created_at',
      {'payor': [
          'id',
          'readable_id',
          'billing_firstname',
          'billing_lastname',
          'info_name'
        ]
      },
      {'subscription': [
        'subscription_name',
        featureFlags.ACCOUNT_ID ? 'account_id' : ''
        ]
      }
    ]
  }
] : oldInvoicesQueryFields;

const oldInvoicesFilter = {
  initializationComplete: true,
  inputs: {
    invoice_number: null,
    invoice_date_start: null,
    invoice_date_end: null,
    payor_id: null,
    payor_name: null,
    payor_company_name: null,
  }
};

class InvoiceListPage extends React.Component {
  state = {
    pageInitializationComplete: false,
    pageInitializationErrors: [],
    invoices: null,
    invoicesQuery: {
      inProgress: false,
      errorState: false,
      userMessage: null
    },
    invoicesFilter: featureFlags.SUBSCRIPTION_NAME ? {
      initializationComplete: true,
      inputs: {
        invoice_number: null,
        invoice_date_start: null,
        invoice_date_end: null,
        payor_id: null,
        subscription_name: null,
        payor_company_name: null,
        ...(featureFlags.ACCOUNT_ID ? { account_id: null } : {})
      }
    } : oldInvoicesFilter
  };

  invoicesDataTableColumns = featureFlags.INVOICE_SEND ? [
    { id: 'invoice_number', heading: 'Invoice #' },
    { id: 'invoice_date', heading: 'Invoice Date' },
    { id: 'account_id', heading: 'Acct. ID' },
    { id: 'payor_id', heading: 'Sub. ID' },
    { id: 'subscription_name', heading: 'Sub. Name' },
    { id: 'payor_billing_name', heading: 'Primary Contact' },
    { id: 'invoice_amount', heading: 'Invoice Amount' },
    { id: 'invoice_balance', heading: 'Invoice Balance' },
    { id: 'resend', heading: '' },
    { id: 'pdf', heading: '' }
  ] : featureFlags.ACCOUNT_ID ? [
    { id: 'invoice_number', heading: 'Invoice #' },
    { id: 'invoice_date', heading: 'Invoice Date' },
    { id: 'account_id', heading: 'Acct. ID' },
    { id: 'payor_id', heading: 'Sub. ID' },
    { id: 'subscription_name', heading: 'Sub. Name' },
    { id: 'payor_billing_name', heading: 'Primary Contact' },
    { id: 'payor_company_name', heading: 'Billing Name' },
    { id: 'invoice_amount', heading: 'Invoice Amount' },
    { id: 'invoice_balance', heading: 'Invoice Balance' },
  ] : featureFlags.SUBSCRIPTION_NAME ? [
    { id: 'invoice_number', heading: 'Invoice #' },
    { id: 'invoice_date', heading: 'Invoice Date' },
    { id: 'payor_id', heading: 'Sub. ID' },
    { id: 'subscription_name', heading: 'Sub. Name' },
    { id: 'payor_billing_name', heading: 'Primary Contact' },
    { id: 'payor_company_name', heading: 'Billing Name' },
    { id: 'invoice_amount', heading: 'Invoice Amount' },
    { id: 'invoice_balance', heading: 'Invoice Balance' },
  ] : [
    { id: 'invoice_number', heading: 'Invoice #' },
    { id: 'invoice_date', heading: 'Invoice Date' },
    { id: 'payor_id', heading: 'Sub. ID' },
    { id: 'payor_billing_name', heading: 'Primary Contact' },
    { id: 'payor_company_name', heading: 'Billing Name' },
    { id: 'invoice_amount', heading: 'Invoice Amount' },
    { id: 'invoice_balance', heading: 'Invoice Balance' },
  ];

  invoicesQueryOptions = {
    sortedBy: null,
    limit: null,
    total: null,
    offset: null
  };

  facilityId;
  facility = null;


  constructor(props) {
    super(props);

    const urlSearchParams = new URLSearchParams(window.location.search);
    this.facilityId = urlSearchParams.get('facility');
  }


  componentDidMount() {
    const initializationTasks = [];

    if (this.facilityId !== null) {
      initializationTasks.push(new Promise((resolve, reject) => {
        getFacility(this.facilityId).then(facility => {
          this.facility = facility;
          resolve();
        }, errors => {
          reject(errors);
        });
      }));
    }

    Promise.all(initializationTasks).then(() => {
      this.setState({
        pageInitializationComplete: true
      }, () => {
        this.getInvoices();
      });
    }, errors => {
      this.setState({
        pageInitializationComplete: true,
        pageInitializationErrors: errors.map(({ message }) => message)
      });
    });
  }

  getInvoices() {
    const invoicesQuery = {
      ...this.state.invoicesQuery,
      inProgress: true,
      errorState: false,
      userMessage: null
    };

    this.setState({ invoicesQuery }, () => {
      const options = this.getInvoicesQueryOptions();

      queryListInvoices(options, invoiceQueryFields).then(response => {
        if (response.errors) {
          const invoicesQuery = {
            ...this.state.invoicesQuery,
            inProgress: false,
            errorState: true,
            userMessage: 'An error occurred while retrieving the list of invoices.'
          }
          this.setState({ invoicesQuery });
          return;
        }

        this.invoicesQueryOptions.total = response.data.listInvoices.total;
        const invoices = this.getInvoicesDataTableRows(response.data.listInvoices.invoices);
        const invoicesQuery = {
          ...this.state.invoicesQuery,
          inProgress: false,
          errorState: false,
          userMessage: null
        };

        this.setState({ invoices, invoicesQuery });
      });
    });
  }


  getInvoicesQueryOptions() {
    const { invoicesFilter } = this.state;
    const invoicesFilterInputs = invoicesFilter.inputs;

    const queryOptions = {};

    const invoiceFilterExpressions = [];
    const payorFilterExpressions = [];
    const subscriptionFilterExpressions = [];
    const filterExpressions = [];

    if (invoicesFilterInputs.invoice_number !== null) {
      invoiceFilterExpressions.push({
        op: 'AND',
        filters: [{
          field: 'readable_id',
          op: 'SUBSTRING',
          value: invoicesFilterInputs.invoice_number
        }]
      });
    }

    if (invoicesFilterInputs.invoice_date_start !== null) {
      const parsedInvoiceDateStart = new Date(invoicesFilterInputs.invoice_date_start);
      const utcParsedInvoiceDateStart = new Date(Date.UTC(
        parsedInvoiceDateStart.getFullYear(),
        parsedInvoiceDateStart.getMonth(),
        parsedInvoiceDateStart.getDate()
      ));
      const utcParsedInvoiceDateStartString = utcParsedInvoiceDateStart.toUTCString();

      invoiceFilterExpressions.push({
        op: 'AND',
        filters: [{
          field: 'created_at',
          op: 'GTE',
          value: utcParsedInvoiceDateStartString
        }]
      });
    }

    if (invoicesFilterInputs.invoice_date_end !== null) {
      const parsedInvoiceDateStart = new Date(invoicesFilterInputs.invoice_date_end);
      const utcParsedInvoiceDateStart = new Date(Date.UTC(
        parsedInvoiceDateStart.getFullYear(),
        parsedInvoiceDateStart.getMonth(),
        parsedInvoiceDateStart.getDate(),
        23, 59, 59
      ));
      const utcParsedInvoiceDateStartString = utcParsedInvoiceDateStart.toUTCString();

      invoiceFilterExpressions.push({
        op: 'AND',
        filters: [{
          field: 'created_at',
          op: 'LTE',
          value: utcParsedInvoiceDateStartString
        }]
      });
    }

    if (invoicesFilterInputs.payor_id !== null) {
      payorFilterExpressions.push({
        op: 'AND',
        filters: [{
          field: 'readable_id',
          op: 'EQ',
          value: invoicesFilterInputs.payor_id
        }]
      });
    }

    if (invoicesFilterInputs.payor_company_name !== null) {
      payorFilterExpressions.push({
        op: 'AND',
        filters: [{
          field: 'info_name',
          op: 'SUBSTRING',
          value: invoicesFilterInputs.payor_company_name
        }]
      });
    }

    if(featureFlags.SUBSCRIPTION_NAME){

      if (invoicesFilterInputs.subscription_name !== null) {
        subscriptionFilterExpressions.push({
          op: 'AND',
          filters: [{
            field: 'subscription_name',
            op: 'EQ',
            value: invoicesFilterInputs.subscription_name.trim()
          }]
        });
      }

    }

    if(featureFlags.ACCOUNT_ID){
      if (invoicesFilterInputs.account_id !== null) {
        subscriptionFilterExpressions.push({
          op: 'AND',
          filters: [{
            field: 'account_id',
            op: 'SUBSTRING',
            value: invoicesFilterInputs.account_id.trim().replace(/\D/g, '')
          }]
        });
      }
    }

    if(!featureFlags.SUBSCRIPTION_NAME){

      if (invoicesFilterInputs.payor_name !== null) {
        const payorNameInputParts = invoicesFilterInputs.payor_name.split(' ');

        const payorNameFilters = [];
        for (let payorNameInputPart of payorNameInputParts) {
          payorNameFilters.push({
            field: 'billing_firstname',
            op: 'SUBSTRING',
            value: payorNameInputPart
          });
          payorNameFilters.push({
            field: 'billing_lastname',
            op: 'SUBSTRING',
            value: payorNameInputPart
          });
        }

        payorFilterExpressions.push({
          op: 'OR',
          filters: payorNameFilters
        });
      }

    }

    if (invoiceFilterExpressions.length) {
      filterExpressions.push({
        model: 'INVOICE',
        op: 'AND',
        sub_expressions: invoiceFilterExpressions
      });
    }

    if (payorFilterExpressions.length) {
      filterExpressions.push({
        model: 'PAYOR',
        op: 'AND',
        sub_expressions: payorFilterExpressions
      });
    }

    if (subscriptionFilterExpressions.length) {
      filterExpressions.push({
        model: 'SUBSCRIPTION',
        op: 'AND',
        sub_expressions: subscriptionFilterExpressions
      });
    }

    if (this.facilityId !== null) {
      filterExpressions.push({
        model: 'BILLING_PROFILE',
        op: 'AND',
        sub_expressions: {
          op: 'AND',
          filters: [{
            field: 'facility_id',
            op: 'EQ',
            value: this.facilityId
          }]
        }
      });
    }

    if (filterExpressions.length) {
      queryOptions.filter_expressions = filterExpressions;
    }

    if (this.invoicesQueryOptions.limit !== null) {
      queryOptions.limit = this.invoicesQueryOptions.limit;
    }

    if (this.invoicesQueryOptions.offset !== null) {
      queryOptions.offset = this.invoicesQueryOptions.offset;
    }

    if (this.invoicesQueryOptions.sortedBy !== null) {
      let model, field;
      switch (this.invoicesQueryOptions.sortedBy.columnId) {
        case 'invoice_number':
          model = 'INVOICE';
          field = 'readable_id';
          break;
        case 'invoice_date':
          model = 'INVOICE';
          field = 'created_at';
          break;
        case 'account_id':
          model = 'SUBSCRIPTION';
          field = 'account_id';
          break;
        case 'payor_id':
          model = 'PAYOR';
          field = 'readable_id';
          break;
        case 'subscription_name':
          model = 'SUBSCRIPTION';
          field = 'subscription_name';
          break;
        case 'payor_billing_name':
          model = 'PAYOR';
          field = 'billing_lastname';
          break;
        case 'invoice_amount':
          model = 'INVOICE';
          field = 'amount';
          break;
        case 'invoice_balance':
          model = 'INVOICE';
          field = 'balance';
          break;
        default:
      }
      queryOptions.order = {
        model,
        field,
        direction: this.invoicesQueryOptions.sortedBy.direction
      }
    }
    return queryOptions;
  }


  getInvoicesDataTableRows(invoices) {
    let rows = [];

    for (let invoice of invoices) {
      let row = [];
      for (let column of this.invoicesDataTableColumns) {
        let cell = null;
        switch (column.id) {
          case 'invoice_number':
            cell = `<a href="?page=parkchirp_invoice&invoice=${invoice.id}${(this.facilityId !== null) ? `&facility=${this.facilityId}` : ''}">${invoice.readable_id}</a>`;
            break;
          case 'invoice_date':
            cell = new Date(Number(invoice.created_at)).toLocaleDateString('en-US');
            break;
          case 'account_id':
            cell = `AC-${invoice.subscription.account_id}`;
            break;
          case 'payor_id':
            cell = invoice.payor.readable_id;
            break;
          case 'subscription_name':
            cell = `${invoice.subscription.subscription_name || ''}`;
            break;
          case 'payor_billing_name':
            cell = `${invoice.payor.billing_firstname} ${invoice.payor.billing_lastname}`;
            break;
          case 'payor_company_name':
            cell = `${invoice.payor.info_name || ''}`
            break;
          case 'invoice_amount':
            cell = (invoice.amount / 100).toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD'
            });
            break;
          case 'invoice_balance':
            cell = (invoice.balance / 100).toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD'
            });
            break;
          case 'resend':
            cell = this.renderResendButton(invoice.id)
            break;
          case 'pdf':
            cell = this.renderPDFButton(invoice.id)
            break;
          default:
        }
        row.push(cell);
      }
      rows.push(row);
    }

    return rows;
  }

  resendInvoiceEmail = async(invoiceId) => {
    try {
      const response = await queryResendInvoiceEmail({ invoice_id: invoiceId });
      if (!response.ok) throw response;
    } catch (err) {
      if (err.text) {
        const errorMessage  = await err.text();
        alert('An error occurred while sending email:\n\n' + errorMessage);
      }
      else {
        alert('An error occurred while sending email.');
      }
    }
  }

  downloadInvoice = async (invoiceId) => {
    try {
      const response = await queryGetInvoicePDF({ invoice_id: invoiceId })
      if (!response.ok) throw response;
      const blob = await response.blob();
      const blobData = await blob.text();
      const binary = atob(blobData.replace(/\s/g, ''));
      const length = binary.length;
      const buffer = new ArrayBuffer(length);
      const view = new Uint8Array(buffer);
      for (let i = 0; i < length; i++) {
        view[i] = binary.charCodeAt(i);
      }
      const pdfBlob = new Blob( [view], { type: 'application/pdf' } );
      if (navigator.msSaveBlob) {
        navigator.msSaveBlob(pdfBlob, 'invoice.pdf');
      }
      else {
        let linkElement = document.createElement('a');
        let url = window.URL;
        let objectUrl;
        linkElement.type = 'application/pdf';
        linkElement.download = 'invoice.pdf';
        linkElement.href = objectUrl = url.createObjectURL(pdfBlob);
        linkElement.dispatchEvent(new MouseEvent('click'));
        setTimeout(url.revokeObjectURL(objectUrl));
      }
    } catch (error) {
      if (error.text) {
        const errorMessage = await error.text();
        alert('An error occurred while retrieving the invoice PDF:\n\n' + errorMessage);
      }
      else {
        alert('An error occurred while retrieving the invoice PDF.');
      }
    };
  }

  getInvoicesDataExport() {
    queryGetInvoiceReport(this.getInvoicesQueryOptions())
      .then(response => response.blob())
      .then(csvBlob => {
        if (navigator.msSaveBlob) {
          navigator.msSaveBlob(csvBlob, 'invoices.csv');
        }
        else {
          let linkElement = document.createElement('a');
          let url = window.URL;
          let objectUrl;
          linkElement.type = 'text/csv';
          linkElement.download = 'invoices.csv';
          linkElement.href = objectUrl = url.createObjectURL(csvBlob);
          linkElement.dispatchEvent(new MouseEvent('click'));
          setTimeout(url.revokeObjectURL(objectUrl));
        }
      });
  }



  // -- Invoice Database Filter Form Actions & Event Handlers


  _getInvoicesFilterInputOnChange(inputId) {
    return value => {
      const inputs = _.cloneDeep(this.state.invoicesFilter.inputs);
      inputs[inputId] = value;

      const invoicesFilter = _.cloneDeep(this.state.invoicesFilter);
      invoicesFilter.inputs = inputs;

      this.setState({ invoicesFilter });
    };
  }


  resetInvoicesFilterForm() {
    const inputs = _.cloneDeep(this.state.invoicesFilter.inputs);
    for (let input in inputs) {
      inputs[input] = null;
    }

    const invoicesFilter = _.cloneDeep(this.state.invoicesFilter);
    invoicesFilter.inputs = inputs;

    this.setState({ invoicesFilter }, this.getInvoices);
  }


  handleInvoicesFilterFormSubmitClick = () => {
    this.getInvoices();
  }


  handleInvoicesFilterFormResetClick = () => {
    this.resetInvoicesFilterForm();
  }



  // -- Invoice Data Table Event Handlers


  handleInvoicesDataTableExportToCSVClick = () => {
    this.getInvoicesDataExport();
  }


  handleInvoicesDataTableSortRequest = data => {
    this.invoicesQueryOptions.sortedBy = data;
    this.getInvoices();
  }


  handleInvoicesDataTableLimitChange = data => {
    this.invoicesQueryOptions.limit = data;
    this.invoicesQueryOptions.offset = null;
    this.getInvoices();
  }


  handleInvoicesDataTablePageChange = data => {
    let offset = ((data - 1) * this.invoicesQueryOptions.limit);
    this.invoicesQueryOptions.offset = offset;
    this.getInvoices();
  }



  // -- Rendering

  renderResendButton(invoiceId) {
    return(
      <div className="parkchirp--invoice-database--invoice-table-action">
        <button
          type="button"
          onClick={() => this.resendInvoiceEmail(invoiceId)}
          id="parkchirp--invoice-database--invoice-resend-action"
        >
          Resend
        </button>
      </div>
    )
  }

  renderPDFButton(invoiceId) {
    return(
      <div className="parkchirp--invoice-database--invoice-table-action">
        <button
          type="button"
          onClick={() => this.downloadInvoice(invoiceId)}
          id="parkchirp--invoice-database--invoice-pdf-action"
        >
          PDF
        </button>
      </div>
    )
  }

  renderNavigationBreadcrumbs() {
    const breadcrumbs = [];
    breadcrumbs.push(getBillingDashboardPageBreadcrumb(false, this.facilityId));
    breadcrumbs.push(getInvoiceListPageBreadcrumb());

    return (
      <NavigationBreadcrumbs breadcrumbs={breadcrumbs} />
    );
  }


  renderPageHeader() {
    const facilityParam = (this.facilityId === null) ? '' :
      `&facility=${this.facilityId}`;

    const heading = 'Invoices';
    const subheading = (this.facility === null) ? null :
      this.facility.fields.facility_name.value;
    const actions = [
      <a href={`?page=parkchirp_invoice${facilityParam}`}
        className="button button-primary">
          Create New Invoice
      </a>
    ];

    return (
      <PageHeader heading={heading}
        subheading={subheading}
        actions={actions}
      />
    );
  }


  renderPageInitializationBlock() {
    const {
      pageInitializationComplete,
      pageInitializationErrors
    } = this.state;

    return (
      <React.Fragment>
        {(!pageInitializationComplete) &&
          <div className="parkchirp--page--user-message-container">
            <ProcessingIndicator userMessage="Initializing, please wait..." />
          </div>
        }
        {(pageInitializationComplete && pageInitializationErrors.length > 0) &&
          <div className="parkchirp--page--user-message-container">
            <div className="parkchirp--page--user-message">
              <p>The following errors occurred while initializing the page:</p>
              <ul>
                {pageInitializationErrors.map(error =>
                  <li key={performance.now()}>{error}</li>
                )}
              </ul>
            </div>
          </div>
        }
      </React.Fragment>
    );
  }


  renderPageMainContent() {
    return (
      <React.Fragment>
        {this.renderFilterForm()}
        {this.renderInvoicesTable()}
      </React.Fragment>
    );
  }


  renderFilterForm() {
    const { invoicesFilter } = this.state;

    return (
      <div className="parkchirp--invoice-database--filter-form">
        <h2>Filter</h2>
        <div className="parkchirp--invoice-database--filter-inputs">
          <div className="parkchirp--invoice-database--filter-input-block">
            <Field label="Invoice #"
              id="parkchirp--invoice-database-filter--invoice-num"
              layout="columns">
              <InputTextField inputId="parkchirp--invoice-database-filter--invoice-num"
                value={invoicesFilter.inputs.invoice_number}
                onChange={this._getInvoicesFilterInputOnChange('invoice_number')}
                editMode={invoicesFilter.initializationComplete}
              />
            </Field>
            {featureFlags.ACCOUNT_ID &&
              <Field label="Acct. ID"
                id="parkchirp--invoice-database-filter--account-id"
                layout="columns"
              >
                <InputTextField inputId="parkchirp--invoice-database-filter--account-id"
                  value={invoicesFilter.inputs.account_id}
                  onChange={this._getInvoicesFilterInputOnChange('account_id')}
                  editMode={invoicesFilter.initializationComplete}
                />
              </Field>
            }
          </div>
          <div className="parkchirp--invoice-database--filter-input-block">
          <Field label="Start Date"
              id="parkchirp--invoice-database-filter--invoice-date-start"
              layout="columns">
              <InputTextField inputId="parkchirp--invoice-database-filter--invoice-date-start"
                value={invoicesFilter.inputs.invoice_date_start}
                onChange={this._getInvoicesFilterInputOnChange('invoice_date_start')}
                editMode={invoicesFilter.initializationComplete}
              />
            </Field>
            <Field label="End Date"
              id="parkchirp--invoice-database-filter--invoice-date-end"
              layout="columns">
              <InputTextField inputId="parkchirp--invoice-database-filter--invoice-date-end"
                value={invoicesFilter.inputs.invoice_date_end}
                onChange={this._getInvoicesFilterInputOnChange('invoice_date_end')}
                editMode={invoicesFilter.initializationComplete}
              />
            </Field>
          </div>
          <div className="parkchirp--invoice-database--filter-input-block">
            <Field label="Sub. ID"
              id="parkchirp--invoice-database-filter--payor-id"
              layout="columns">
              <InputTextField inputId="parkchirp--invoice-database-filter--payor-id"
                value={invoicesFilter.inputs.payor_id}
                onChange={this._getInvoicesFilterInputOnChange('payor_id')}
                editMode={invoicesFilter.initializationComplete}
              />
            </Field>

            {!featureFlags.SUBSCRIPTION_NAME &&
              <Field label="PC Name"
                id="parkchirp--invoice-database-filter--payor-name"
                layout="columns">
                <InputTextField inputId="parkchirp--invoice-database-filter--payor-name"
                  value={invoicesFilter.inputs.payor_name}
                  onChange={this._getInvoicesFilterInputOnChange('payor_name')}
                  editMode={invoicesFilter.initializationComplete}
                />
              </Field>
            }

            {featureFlags.SUBSCRIPTION_NAME &&
              <Field label="Sub. Name"
                id="parkchirp--invoice-database-filter--sub-name"
                layout="columns">
                <InputTextField inputId="parkchirp--invoice-database-filter--sub-name"
                  value={invoicesFilter.inputs.subscription_name ? invoicesFilter.inputs.subscription_name : ""}
                  onChange={this._getInvoicesFilterInputOnChange('subscription_name')}
                  editMode={invoicesFilter.initializationComplete}
                />
              </Field>
            }

          </div>
        </div>
        <div className="parkchirp--invoice-database--filter-actions">
          <button type="button"
            className="button button-primary parkchirp--invoice-database--filter-action"
            onClick={this.handleInvoicesFilterFormSubmitClick}>
            Filter
          </button>
          <button type="button"
            className="button parkchirp--invoice-database--filter-action"
            onClick={this.handleInvoicesFilterFormResetClick}>
            Reset
          </button>
        </div>
      </div>
    );
  }


  renderInvoicesTable() {
    const { invoices, invoicesQuery } = this.state;

    if (invoicesQuery.inProgress) {
      return (
        <div className="parkchirp--page--user-message-container">
          <ProcessingIndicator userMessage="Retrieving Invoices..." />
        </div>
      );
    }

    if (invoicesQuery.errorState) {
      return (
        <div className="parkchirp--page--user-message-container">
          <p>{invoicesQuery.userMessage}</p>
        </div>
      );
    }

    if (invoices !== null) {
      return (
        <div className="parkchirp--invoice-database--invoice-table">
          {(invoices.length > 0) &&
            <div className="parkchirp--invoice-database--invoice-table-actions">
              <div className="parkchirp--invoice-database--invoice-table-action">
                <button type="button"
                  id="parkchirp--invoice-database--invoice-export-to-csv-action"
                  className={featureFlags.INVOICE_SEND ? "button button-primary" : ""}
                  onClick={this.handleInvoicesDataTableExportToCSVClick}>
                  Export to CSV
                </button>
              </div>
            </div>
          }
          <DataTable id="invoice-database"
            columns={this.invoicesDataTableColumns}
            rows={invoices}
            overallRowCount={this.invoicesQueryOptions.total}
            currentOffset={this.invoicesQueryOptions.offset}
            currentLimit={this.invoicesQueryOptions.limit}
            sortedBy={this.invoicesQueryOptions.sortedBy}
            onSortRequest={this.handleInvoicesDataTableSortRequest}
            onLimitChange={this.handleInvoicesDataTableLimitChange}
            onPageChange={this.handleInvoicesDataTablePageChange}
          />
        </div>
      );
    }

    return null;
  }


  render() {
    const {
      pageInitializationComplete,
      pageInitializationErrors
    } = this.state;

    return (
      <div className="parkchirp--page parkchirp--page--invoice-database">
        {this.renderNavigationBreadcrumbs()}
        {this.renderPageHeader()}
        <div className="parkchirp--page-content">
          {(!pageInitializationComplete || pageInitializationErrors.length > 0) &&
            this.renderPageInitializationBlock()
          }
          {(pageInitializationComplete && pageInitializationErrors.length === 0) &&
            this.renderPageMainContent()
          }
        </div>
      </div>
    );
  }
}
export default InvoiceListPage;


export function getInvoiceListPageBreadcrumb(currentPage = true, facilityId = null) {
  const title = 'Invoices';
  const href = (currentPage) ? null :
    `?page=parkchirp_invoices${(facilityId !== null) ? `&facility=${facilityId}` : ''}`;

  return { title, href };
}
